import { useQuery } from '@tanstack/react-query';

import {
	getActiveOperatorTicketsOptions,
	type OperatorTicketsResponseView,
	type TicketShopPriceGroupView,
	type TicketShopTicketView,
} from '@mopla/ticketshop-api';

import {
	IOperatorTickets,
	IPriceGroupTab,
	ITicketCard,
	PricingModel,
	TicketCategory,
} from '../types';

interface IUseOperatorTicketsResult {
	operatorTickets: IOperatorTickets | null;
	isLoading: boolean;
	error: Error | null;
}

/**
 * Map BE DTO → internal model. BE fields are optional in the generated types;
 * default the required app fields defensively.
 */
/**
 * Derive the legacy ticket class the FE still switches on from the new fields
 * (the BE dropped `category`/`ticketType`, ADR 0015): stop-based → short-trip,
 * a 12-month ticket → subscription (Jahresticket), everything else time-bound.
 */
export const deriveTicketCategory = (dto: {
	pricingModel?: string;
	period?: string;
	periodCount?: number;
}): TicketCategory => {
	if (dto.pricingModel === 'stop-based') return 'short-trip';
	if (dto.period === 'month' && dto.periodCount === 12) return 'subscription';
	return 'time-bound';
};

const mapTicket = (dto: TicketShopTicketView): ITicketCard => ({
	id: dto.id ?? '',
	name: dto.name ?? '',
	category: deriveTicketCategory(dto),
	pricingModel: (dto.pricingModel ?? 'zone-based') as PricingModel,
	billingMode: dto.billingMode ?? 'single_charge',
	period: dto.period,
	periodCount: dto.periodCount,
	startingPriceInCents: dto.startingPriceInCents ?? 0,
});

const mapPriceGroup = (dto: TicketShopPriceGroupView): IPriceGroupTab => ({
	id: dto.id ?? '',
	displayCode: dto.displayCode ?? '',
	name: dto.name ?? '',
	tickets: [...(dto.tickets ?? [])].map(mapTicket),
	conditionsUrl: dto.conditionsUrl,
	legalText: dto.legalText,
});

const mapOperatorTickets = (
	dto: OperatorTicketsResponseView
): IOperatorTickets => ({
	operatorId: dto.operatorId ?? '',
	tariffId: dto.tariffId,
	tariffConditionsUrl: dto.tariffConditionsUrl,
	priceGroups: [...(dto.priceGroups ?? [])].map(mapPriceGroup).sort((a, b) => {
		const byCode = a.displayCode.localeCompare(b.displayCode, 'de');
		return byCode !== 0 ? byCode : a.name.localeCompare(b.name, 'de');
	}),
});

/**
 * Step 1 — Ticket selection. GET /api/v1/operators/{operatorId}/tickets
 * Unknown operator OR no current tariff → 200 with empty priceGroups.
 */
export const useOperatorTickets = (
	operatorId: string | undefined
): IUseOperatorTicketsResult => {
	const { data, isLoading, error } = useQuery({
		...getActiveOperatorTicketsOptions({
			path: { operatorId: operatorId ?? '' },
		}),
		enabled: !!operatorId,
		select: mapOperatorTickets,
	});

	return { operatorTickets: data ?? null, isLoading, error };
};
